import * as React from 'react';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import styled from 'styled-components';

import { FONT_SIZE, SPACING } from '../../../../packages/constants';
import { Text } from '../../../text';
import { ArticleData } from '../../release-notes';
import {
    StyledNewReleaseAllProducts,
    StyledNewReleaseDashboard,
    StyledNewReleaseForecasting,
    StyledNewReleaseMulti,
    StyledNewReleaseReporting,
    StyledNewReleaseSustain,
} from '../icons/icons';

dayjs.extend(utc);

export interface ArticleProps {
    /**
     * The article data.
     */
    data: ArticleData;
    /**
     * Show videos and images
     */
    showMedia?: boolean;
    /**
     * Handles click event on video content.
     */
    handleOpenVideo: (itemId: string, itemURL: string) => void;
}

export interface ProductNameProps {
    colour?: string;
}

const productCategories = [
    {
        id: 'all-products',
        name: 'All Products',
        colour: '#435c84',
        icon: <StyledNewReleaseAllProducts />,
    },
    {
        id: 'dashboard',
        name: 'Dashboard',
        colour: '#ffd943',
        icon: <StyledNewReleaseDashboard />,
    },
    {
        id: 'forecasting',
        name: 'Forecasting ',
        colour: '#40d98c',
        icon: <StyledNewReleaseForecasting />,
    },
    {
        id: 'multi',
        name: 'Multi',
        colour: 'hsla(285.0980392156863, 100%, 70%, 1)',
        icon: <StyledNewReleaseMulti />,
    },
    {
        id: 'reporting',
        name: 'Reporting ',
        colour: '#40c5ff',
        icon: <StyledNewReleaseReporting />,
    },
    {
        id: 'sustain',
        name: 'Sustain ',
        colour: '#000000',
        icon: <StyledNewReleaseSustain />,
    },
];

const headerColour = '#8292ad';
const Header = styled.div`
    display: flex;
    align-items: center;
    border-top-left-radius: 4px;
    border-top-right-radius: 4px;
    margin-bottom: 10px;
    font-size: ${FONT_SIZE.X_SMALL};
    line-height: ${FONT_SIZE.X_SMALL};
    letter-spacing: 0.6px;
    color: ${headerColour};
`;

const ProductName = styled.div<ProductNameProps>`
    margin-left: ${SPACING.SMALL};
    color: ${(props) => props.colour || '#435c84'};
    font-weight: 400;
    text-transform: uppercase;
`;

const Divider = styled.div`
    width: 1px;
    height: 12px;
    margin-right: ${SPACING.MEDIUM};
    margin-left: ${SPACING.MEDIUM};
    background-color: ${headerColour};
    color: ${headerColour};
`;

const spotlightBrandColor = '#04255b';
const Title = styled.div`
    white-space: nowrap;
    margin-bottom: 10px;
    font-size: ${FONT_SIZE.LARGE};
    font-weight: 500;
    line-height: 1.4;
    overflow: hidden;
    text-overflow: ellipsis;
    color: ${spotlightBrandColor};
`;

const ThumbnailContainer = styled.div`
    max-width: 420px;
    height: auto;
    margin-bottom: 10px;
`;

const StyledImage = styled.img`
    display: block;
    width: 100%;
    height: auto;
`;

const ImageThumbnail = styled(StyledImage)`
    border-radius: 2px;
`;

const VideoThumbnail = styled(StyledImage)`
    position: relative;
`;

const VideoPlayContainer = styled.div`
    position: absolute;
    width: 56px;
    padding-right: 0;
    opacity: 0.6;
`;

const VideoPlayImage = styled.img`
    border: 0;
    vertical-align: middle;
    display: inline-block;
    max-width: 100%;
    margin-top: 0;
    margin-bottom: 0;
`;

const VideoButton = styled.button`
    display: flex;
    position: relative;
    overflow: hidden;
    width: 100%;
    height: 100%;
    max-width: none;
    margin-right: 20px;
    justify-content: center;
    align-items: center;
    border: none;
    padding: 0;
    cursor: pointer;
`;

const LearnMoreLink = styled.a`
    text-transform: capitalize;
    display: inline-block;
    text-align: center;
    margin-top: ${SPACING.MEDIUM};
    font-size: ${FONT_SIZE.X_SMALL};
    font-weight: 500;
    letter-spacing: 0.6px;
    color: #00b1ff;
`;

export const Article = ({ data, showMedia, handleOpenVideo }: ArticleProps) => {
    const defaultCategory = productCategories.find((category) => category.id === 'all-products');
    const product = productCategories.find((category) => category.id === data.product) || defaultCategory;

    const thumbnailData = data.videoThumbnails || data.thumbnailImage;

    const openVideo = (e: any) => {
        e.preventDefault();
        handleOpenVideo(data.videoLink?.id as string, data.videoLink?.url as string);
    };

    const video = showMedia && data.videoThumbnails && (
        <VideoButton onClick={openVideo}>
            <VideoThumbnail src={thumbnailData?.url} alt={thumbnailData?.alt} />
            <VideoPlayContainer>
                <VideoPlayImage
                    src="data:image/png;base64,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"
                    loading="lazy"
                    alt=""
                ></VideoPlayImage>
            </VideoPlayContainer>
        </VideoButton>
    );

    const image = showMedia && data.thumbnailImage && (
        <ImageThumbnail src={thumbnailData?.url} alt={thumbnailData?.alt} />
    );

    const thumbnail = video || image;

    return (
        <article>
            <Header>
                {product?.icon}
                <ProductName colour={product?.colour}> {`${product?.name}`} </ProductName>
                <Divider />
                {`${dayjs.utc(data.releaseDate).format('DD MMMM YYYY')}`}
            </Header>
            <Title>{data.name}</Title>
            {thumbnailData && <ThumbnailContainer> {thumbnail} </ThumbnailContainer>}
            <Text fontSize={FONT_SIZE.SMALL} color={spotlightBrandColor} marginBottom={'0'}>
                {data.postSummary}
            </Text>
            {data.learnMoreLink && (
                <LearnMoreLink href={data.learnMoreLink} target={'_blank'}>
                    Learn More
                </LearnMoreLink>
            )}
        </article>
    );
};
